// A table's View and Columns menus (lib/table-views.ts): the person's saved views to switch between, saving what the
// table shows now as a view, and ticking which columns show. DataTable puts them in its page's header when the page has a
// place for them (ListPage), else in its footer.
import { Check, ChevronDown, Columns3, LayoutList } from "lucide-react";
import { type ReactNode, type PointerEvent as ReactPointerEvent, useState } from "react";
import type { TableView } from "../../../shared/db.gen.ts";
import { navigate, useSearch } from "../lib/router.tsx";
import { type Layout, layoutOf, useTableViews, viewSearch, withViewSearch } from "../lib/table-views.ts";
import { cx, Input } from "./controls.tsx";
import { Popover } from "./Popover.tsx";
import { useTableText } from "./table-text.ts";

const item = "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-hover";
const action = "rounded px-2 py-1 text-left text-accent-text hover:bg-hover";

/** A name typed in place (a new view, or a view's new name): Enter saves, Esc goes back. */
function NameBox({ initial, label, onDone, onCancel }: { initial: string; label: string; onDone: (name: string) => Promise<boolean>; onCancel: () => void }) {
  const t = useTableText();
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    const ok = await onDone(name.trim());
    setBusy(false);
    if (ok) onCancel();
  };
  return (
    <div className="flex flex-col gap-1.5 px-1 py-1">
      <span className="text-[11px] text-muted">{label}</span>
      <Input
        autoFocus
        value={name}
        maxLength={60}
        placeholder={t.namePlaceholder}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void save();
          else if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }
        }}
        className="h-7 text-[12px]"
      />
      <span className="text-[11px] text-faint">{t.nameHint}</span>
    </div>
  );
}

export function ViewMenu({
  views,
  picked,
  changed,
  onPick,
  onSave,
  onSaveAs,
  onRename,
  onDelete,
  onDiscard,
}: {
  views: TableView[];
  picked: TableView | undefined;
  /** The table differs from the picked view (or from Default). */
  changed: boolean;
  onPick: (view: TableView | undefined) => void;
  onSave: () => void;
  onSaveAs: (name: string) => Promise<boolean>;
  onRename: (view: TableView, name: string) => Promise<boolean>;
  onDelete: (view: TableView) => void;
  onDiscard: () => void;
}) {
  const t = useTableText();
  const [naming, setNaming] = useState<"new" | "rename" | null>(null);
  const name = picked?.name ?? t.defaultView;
  return (
    <Popover
      width={280}
      button={(toggle, open) => (
        <button
          type="button"
          onClick={() => {
            setNaming(null);
            toggle();
          }}
          aria-expanded={open}
          title={changed ? t.changedTitle(name) : t.viewsTitle}
          className="inline-flex h-6 max-w-56 items-center gap-1.5 rounded-md border border-line bg-surface px-2 text-[12px] text-muted hover:border-line-strong hover:text-text"
        >
          <LayoutList className="size-3.5 shrink-0" />
          <span className="truncate text-text">{name}</span>
          {changed && <span className="shrink-0 text-faint">· {t.changed}</span>}
          <ChevronDown className="size-3.5 shrink-0 text-faint" />
        </button>
      )}
    >
      {(close) =>
        naming ? (
          <NameBox
            initial={naming === "rename" ? (picked?.name ?? "") : ""}
            label={naming === "rename" ? t.renameLabel(picked?.name ?? "") : t.saveAsLabel}
            onDone={(n) => (naming === "rename" && picked ? onRename(picked, n) : onSaveAs(n))}
            onCancel={() => setNaming(null)}
          />
        ) : (
          <div className="flex flex-col text-[12px]">
            <div className="px-2 pb-1 text-[11px] text-faint">{t.viewsHint}</div>
            {[undefined, ...views].map((v) => (
              <button
                key={v?.id ?? "default"}
                type="button"
                className={item}
                onClick={() => {
                  onPick(v);
                  close();
                }}
              >
                <Check className={cx("size-3.5 shrink-0", picked?.id === v?.id ? "text-accent-text" : "invisible")} />
                <span className={cx("truncate", !v && "text-muted")}>{v ? v.name : t.defaultView}</span>
              </button>
            ))}
            <div className="mt-1 flex flex-col border-t border-line pt-1">
              {changed && <div className="px-2 py-1 text-[11px] text-muted">{t.differs(picked?.name ?? null)}</div>}
              {changed && picked && (
                <button
                  type="button"
                  className={action}
                  onClick={() => {
                    onSave();
                    close();
                  }}
                >
                  {t.saveTo(picked.name)}
                </button>
              )}
              <button type="button" className={action} onClick={() => setNaming("new")}>
                {t.saveAsNew}
              </button>
              {changed && (
                <button
                  type="button"
                  className={cx(action, "text-muted")}
                  onClick={() => {
                    onDiscard();
                    close();
                  }}
                >
                  {t.discard}
                </button>
              )}
              {picked && (
                <div className="flex gap-1">
                  <button type="button" className={cx(action, "text-muted")} onClick={() => setNaming("rename")}>
                    {t.rename}
                  </button>
                  <button
                    type="button"
                    className={cx(action, "text-muted hover:text-danger")}
                    onClick={() => {
                      onDelete(picked);
                      close();
                    }}
                  >
                    {t.remove(picked.name)}
                  </button>
                </div>
              )}
            </div>
          </div>
        )
      }
    </Popover>
  );
}

/** Which columns show, in the table's order (the first always does); widths and order go back with Reset. */
export function ColumnsMenu({
  columns,
  hidden,
  onToggle,
  onShowAll,
  onReset,
  label,
}: {
  columns: { key: string; header: string }[];
  hidden: ReadonlySet<string>;
  onToggle: (key: string, on: boolean) => void;
  onShowAll: () => void;
  /** Widths and order dragged back to the table's own; absent when nothing was dragged. */
  onReset?: () => void;
  label?: ReactNode;
}) {
  const t = useTableText();
  const shown = columns.filter((c) => c.header);
  const hiddenCount = shown.filter((c, i) => i > 0 && hidden.has(c.key)).length;
  return (
    <Popover
      width={240}
      button={(toggle) => (
        <button
          type="button"
          onClick={toggle}
          className="inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[12px] text-muted hover:bg-hover hover:text-text"
          title={t.columnsTitle}
        >
          <Columns3 className="size-3.5" /> {label ?? t.columns}
          {hiddenCount ? t.hidden(hiddenCount) : ""}
        </button>
      )}
    >
      {() => (
        <div className="flex max-h-80 flex-col gap-0.5 overflow-y-auto text-[12px]">
          <div className="px-1.5 pb-1 text-[11px] text-faint">{t.columnsHint}</div>
          {shown.map((c, i) => (
            <label key={c.key} className={cx("flex items-center gap-2 rounded px-1.5 py-1", i > 0 && "hover:bg-hover")}>
              <input
                type="checkbox"
                checked={i === 0 || !hidden.has(c.key)}
                disabled={i === 0}
                onChange={(e) => onToggle(c.key, e.target.checked)}
                className="accent-[var(--accent)]"
              />
              {c.header}
            </label>
          ))}
          {hiddenCount > 0 && (
            <button type="button" className="mt-1 px-1.5 text-left text-accent-text hover:underline" onClick={onShowAll}>
              {t.showAll}
            </button>
          )}
          {onReset && (
            <button type="button" className="mt-1 px-1.5 text-left text-accent-text hover:underline" onClick={onReset}>
              {t.reset}
            </button>
          )}
        </div>
      )}
    </Popover>
  );
}

/** A column a page draws itself (not through DataTable): its key, header and whether it's hidden until shown. */
export type LayoutColumn = { key: string; header: string; optional?: boolean };

/**
 * Columns and saved views for a page that draws its own rows (the Purchasing plan: Mitch, 2026-11-03, "the purchasing
 * plan view doesn't allow us to edit the columns or view layout"): the person's columns shown (optional ones hidden
 * until shown) and their views of it, with the page's filters (its URL's search), as DataTable keeps them. The first
 * column is always shown. Widths drag like DataTable's (a heading's right edge; a double-click gives it back its own), kept
 * in the layout. Returns the columns to draw, in order, their widths dragged (px), a heading's drag handle, and the View
 * and Columns menus for beside the title.
 */
/**
 * Resizing a column moves its edge with the pointer, whatever its neighbours are (Mitch, 2026-11-04: "it seems very
 * inconsistent about the direction that resizing happens"). A column that shares out the room left over (a `1fr`
 * track) would otherwise give or take what the dragged one gains: one before it shrank and the edge stayed put while
 * the column grew leftwards, and the first drag from a default width jumped. So the column dragged and every one before
 * it are pinned at the widths they're drawn at (each resize handle carries `data-resize-key`, in column order), and only
 * the columns after it give way or move along.
 */
export function pinnedWidths(handle: HTMLElement, key: string, widths: Record<string, number> = {}): Record<string, number> {
  const row = handle.parentElement?.parentElement;
  const out = { ...widths };
  for (const h of row?.querySelectorAll<HTMLElement>("[data-resize-key]") ?? []) {
    const k = h.dataset.resizeKey;
    if (!k || !h.parentElement) continue;
    out[k] = Math.round(h.parentElement.getBoundingClientRect().width);
    if (k === key) break;
  }
  return out;
}

export function useColumnLayout<C extends LayoutColumn>(
  all: readonly C[],
  storeKey: string,
): { visible: C[]; menus: ReactNode; widths: Record<string, number>; resizer: (key: string) => ReactNode } {
  const tv = useTableViews(storeKey);
  const params = useSearch();
  const [dragged, setDragged] = useState<Layout | null>(null); // while a heading's edge is being dragged
  const layout: Layout = dragged ?? (tv.pref ? layoutOf(tv.pref) : {});
  const shownKeys = new Set(layout.shown ?? []);
  const hidden = new Set([...(layout.hidden ?? []), ...all.filter((c) => c.optional && !shownKeys.has(c.key)).map((c) => c.key)]);
  const visible = all.filter((c, i) => i === 0 || !hidden.has(c.key));
  const save = (next: Layout) => tv.savePref({ layout: next });
  const toggle = (key: string, on: boolean) => {
    const optional = all.find((c) => c.key === key)?.optional;
    save({
      ...layout,
      hidden: (layout.hidden ?? []).filter((k) => k !== key).concat(on || optional ? [] : [key]),
      shown: (layout.shown ?? []).filter((k) => k !== key).concat(on && optional ? [key] : []),
    });
  };
  const startResize = (e: ReactPointerEvent<HTMLElement>, key: string) => {
    if (!tv.enabled) return;
    e.preventDefault();
    e.stopPropagation();
    const base = layout;
    const pinned = pinnedWidths(e.currentTarget, key, base.widths);
    const w0 = pinned[key] ?? Math.round((e.currentTarget.parentElement as HTMLElement).getBoundingClientRect().width);
    const x0 = e.clientX;
    let w = w0;
    const withWidth = (px: number): Layout => ({ ...base, widths: { ...pinned, [key]: px } });
    const move = (ev: PointerEvent) => {
      w = Math.max(40, Math.min(1200, Math.round(w0 + ev.clientX - x0)));
      setDragged(withWidth(w));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setDragged(null);
      if (w !== w0) save(withWidth(w));
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const ownWidth = (key: string) => {
    const { [key]: _, ...widths } = layout.widths ?? {};
    save({ ...layout, widths });
  };
  const resizer = (key: string) =>
    tv.enabled && (
      <span
        aria-hidden
        data-resize-key={key}
        title="Drag to resize (double-click: its own width)"
        onPointerDown={(e) => startResize(e, key)}
        onDoubleClick={() => ownWidth(key)}
        className="absolute inset-y-0 -right-1 z-10 w-2 cursor-col-resize hover:bg-accent/40"
      />
    );
  const filters = viewSearch(params.toString());
  const picked = tv.views.find((v) => v.id === tv.pref?.view_id);
  const shape = (l: Layout, f: string) =>
    JSON.stringify([[...(l.hidden ?? [])].sort(), [...(l.shown ?? [])].sort(), Object.entries(l.widths ?? {}).sort(([a], [b]) => a.localeCompare(b)), f]);
  const changed = tv.enabled && shape(layout, filters) !== (picked ? shape(layoutOf(picked), picked.search) : shape({}, ""));
  const pick = (v?: TableView) => {
    tv.savePref({ view_id: v?.id ?? null, layout: v ? layoutOf(v) : {}, sort: null });
    navigate(location.pathname + withViewSearch(location.search, v ? v.search : ""), { replace: true });
  };
  const menus = tv.enabled && (
    <>
      <ViewMenu
        views={tv.views}
        picked={picked}
        changed={changed}
        onPick={pick}
        onSave={() => picked && void tv.updateView(picked.id, { layout, sort: null, search: filters })}
        onSaveAs={async (name) => {
          const id = crypto.randomUUID();
          const ok = await tv.createView({ id, table_key: storeKey, name, layout, sort: null, search: filters, position: tv.views.length });
          if (ok) tv.savePref({ view_id: id });
          return ok;
        }}
        onRename={(v, name) => tv.updateView(v.id, { name })}
        onDelete={(v) => tv.removeView(v.id)}
        onDiscard={() => pick(picked)}
      />
      <ColumnsMenu
        columns={[...all]}
        hidden={hidden}
        onToggle={toggle}
        onShowAll={() => save({ ...layout, hidden: [], shown: all.filter((c) => c.optional).map((c) => c.key) })}
        onReset={Object.keys(layout.widths ?? {}).length ? () => save({ hidden: layout.hidden, shown: layout.shown }) : undefined}
      />
    </>
  );
  return { visible, menus, widths: layout.widths ?? {}, resizer };
}
